import axiosInstance from '../api/axiosInstance';

/**
 * Client-side internet speed test.
 *
 * Every measurement below is timed in THIS browser tab, against the user's
 * real network connection — it is never a server-side self-check. The
 * backend only supplies bytes to move (download) and a sink to receive them
 * (upload) / a tiny echo (ping); the clock that matters is `performance.now()`
 * running here.
 */

const ENDPOINTS = {
  ping: 'techsavanna_pos.api.network_speed_api.ping',
  download: 'techsavanna_pos.api.network_speed_api.download_test_payload',
  upload: 'techsavanna_pos.api.network_speed_api.upload_test_payload',
  logTest: 'techsavanna_pos.api.network_speed_api.log_speed_test',
  listTests: 'techsavanna_pos.api.network_speed_api.list_speed_tests',
};

const cacheBust = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// crypto.getRandomValues caps out at 65536 bytes per call — fill in chunks.
const randomBytes = (sizeBytes) => {
  const bytes = new Uint8Array(sizeBytes);
  const chunk = 65536;
  for (let offset = 0; offset < sizeBytes; offset += chunk) {
    const view = bytes.subarray(offset, Math.min(offset + chunk, sizeBytes));
    window.crypto.getRandomValues(view);
  }
  return bytes;
};

// Frappe decodes every request body as UTF-8 before routing (make_form_dict),
// so raw binary bytes 500 the whole request before our handler ever runs.
// Base64-encode the payload so it survives that — btoa() is chunked here to
// avoid blowing the call stack on String.fromCharCode(...bigArray).
const toBase64 = (bytes) => {
  let binary = '';
  const chunk = 32768;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + chunk));
  }
  return window.btoa(binary);
};

/** Round-trip latency + jitter, from `count` sequential pings. */
async function measurePing(count = 5) {
  const samples = [];
  for (let i = 0; i < count; i += 1) {
    const start = performance.now();
    // eslint-disable-next-line no-await-in-loop
    await axiosInstance.get(ENDPOINTS.ping, {
      params: { _: cacheBust() },
      skipGlobalErrorNotification: true,
      timeout: 10000,
    });
    samples.push(performance.now() - start);
  }
  const ping_ms = samples.reduce((a, b) => a + b, 0) / samples.length;
  const jitter_ms = Math.max(...samples) - Math.min(...samples);
  return { ping_ms, jitter_ms };
}

/** Downloads a known-size payload and derives Mbps from elapsed time. */
async function measureDownload(sizeKb = 2048) {
  const start = performance.now();
  const res = await axiosInstance.get(ENDPOINTS.download, {
    params: { size_kb: sizeKb, _: cacheBust() },
    responseType: 'blob',
    skipGlobalErrorNotification: true,
    timeout: 30000,
  });
  const seconds = (performance.now() - start) / 1000;
  const bytes = res?.data?.size || sizeKb * 1024;
  return seconds > 0 ? (bytes * 8) / seconds / 1_000_000 : 0;
}

/** Uploads a known-size random payload and derives Mbps from elapsed time. */
async function measureUpload(sizeKb = 1024) {
  const bytes = randomBytes(sizeKb * 1024);
  const body = JSON.stringify({ data: toBase64(bytes) });
  const wireBytes = new TextEncoder().encode(body).length;

  const start = performance.now();
  await axiosInstance.post(ENDPOINTS.upload, body, {
    headers: { 'Content-Type': 'application/json' },
    skipGlobalErrorNotification: true,
    timeout: 30000,
  });
  const seconds = (performance.now() - start) / 1000;
  return seconds > 0 ? (wireBytes * 8) / seconds / 1_000_000 : 0;
}

const getNetworkType = () => {
  const conn =
    navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection;
  return conn?.effectiveType || conn?.type || '';
};

/**
 * Runs ping → download → upload sequentially (so each leg gets the full
 * connection to itself) and returns a plain result object ready to log.
 */
export async function runClientSpeedTest({
  downloadSizeKb = 2048,
  uploadSizeKb = 1024,
  pingCount = 5,
} = {}) {
  const { ping_ms, jitter_ms } = await measurePing(pingCount);
  const download_mbps = await measureDownload(downloadSizeKb);
  const upload_mbps = await measureUpload(uploadSizeKb);

  return {
    ping_ms: Number(ping_ms.toFixed(1)),
    jitter_ms: Number(jitter_ms.toFixed(1)),
    download_mbps: Number(download_mbps.toFixed(2)),
    upload_mbps: Number(upload_mbps.toFixed(2)),
    network_type: getNetworkType(),
    tested_at: new Date().toISOString(),
  };
}

/** Persist a completed test for the logged-in user. */
export async function saveSpeedTestResult(result, { warehouse, triggerSource } = {}) {
  const res = await axiosInstance.post(
    ENDPOINTS.logTest,
    {
      ...result,
      warehouse: warehouse || undefined,
      trigger_source: triggerSource || result.trigger_source || 'Manual',
    },
    { skipGlobalErrorNotification: true },
  );
  const msg = res?.data?.message ?? res?.data;
  if (msg?.success === false) throw new Error(msg?.message || 'Failed to save speed test');
  return msg?.data ?? msg;
}

/**
 * History for the fluctuation graph — own tests unless the caller is an admin.
 * `per_user_limit` (admin, "all staff" view only) caps how many of each
 * individual user's tests come back, so the shared graph isn't dominated by
 * whoever's device happens to test most often.
 */
export async function fetchSpeedTestHistory({
  user,
  warehouse,
  from_date,
  to_date,
  limit = 200,
  per_user_limit,
} = {}) {
  const res = await axiosInstance.post(
    ENDPOINTS.listTests,
    { user, warehouse, from_date, to_date, limit, per_user_limit },
    { skipGlobalErrorNotification: true },
  );
  const msg = res?.data?.message ?? res?.data;
  if (msg?.success === false) throw new Error(msg?.message || 'Failed to load speed test history');
  return msg?.data ?? { tests: [], summary: {}, by_user: [] };
}

/** Full cycle: run the test, save it, return { result, saved }. */
export async function runAndSaveSpeedTest(opts = {}) {
  const result = await runClientSpeedTest(opts);
  const saved = await saveSpeedTestResult(result, opts);
  return { result, saved };
}
